<template>
  <div class="card shadow-sm my-2">
    <!-- Edit Instructions Slot -->
    <div v-if="$slots.editInstructions">
      <slot name="editInstructions" />
    </div>
  </div>
  <div class="card shadow-sm">
    <!-- Search bar -->
    <div class="card-body d-flex justify-content-between align-items-center border-bottom pb-2">
      <div class="d-flex gap-2">
        <h4 v-if="heading != null && heading != ''" class="d-flex flex-1 gap-2">{{ heading }}</h4>
        <div v-if="$slots.buttons">
          <slot name="buttons" />
        </div>
      </div>
      <div class="d-flex align-items-center justify-items-between gap-3">
        <!-- Setup Wizard -->
        <div v-if="setupWizard">
          <MaterialButton size="sm" variant="success">
            Setup Wizard
          </MaterialButton>
        </div>
        <!-- Sort controls -->
        <div v-if="sortable" class="d-flex align-items-center gap-2 me-2">
          <select class="form-select form-select-sm px-2"
            style="border-radius: 10px; min-width: 120px; border: 1px solid #eee; padding-right: 25px !important;"
            v-model="sortKey" aria-label="Sort by column">
            <option disabled value="">Sort by...</option>
            <option v-for="col in columns" :key="col.key" :value="col.key">
              {{ col.label }}
            </option>
          </select>
          <select class="form-select form-select-sm px-2"
            style="border-radius: 10px; min-width: 120px; border: 1px solid #eee; padding-right: 25px !important;"
            v-model="sortOrder" aria-label="Sort order">
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
        <!-- End sort controls -->
        <!-- ✅ Search input with debounce and emit -->
        <div v-if="searchable" class="input-group d-flex flex-nowrap justify-content-end gap-1" style="width: 250px;">
          <div class="position-relative w-100 px-2">
            <MaterialInput id="search" type="text" v-model="searchInput" size="lg" :placeholder="searchPlaceholder"
              style="min-width: 150px;" @keyup.enter="applySearch" />
            <i v-if="searchInput" class="material-icons position-absolute end-0 top-50 translate-middle-y text-danger"
              style="cursor: pointer; right: 10px !important; font-size: 18px; z-index: 100;" @click="clearSearch">
              close
            </i>
          </div>
          <div>
            <button class="btn btn-success m-0" style="z-index: 0;" @click="applySearch">
              <i class="material-icons">search</i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Table -->
    <div class="table-responsive p-4">
      <table class="table table-hover mb-0">
        <thead class="table-light">
          <tr>
            <th class="text-sm" v-if="$slots.actions">Actions</th>
            <th v-if="selectable" style="width: 50px;">
              <MaterialCheckbox id="select-all" :checked="allSelected" @change="toggleSelectAll" />
            </th>
            <th class="text-sm" v-for="col in columns" :key="col.key">
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading skeleton -->
          <template v-if="loading">
            <tr v-for="i in skeletonRows" :key="`skeleton-${i}`">
              <td v-if="$slots.actions">
                <div class="skeleton skeleton-sm" style="width: 60px;"></div>
              </td>
              <td v-if="selectable">
                <div class="skeleton skeleton-sm" style="width: 20px; height: 20px;"></div>
              </td>
              <td v-for="col in columns" :key="col.key">
                <div class="skeleton skeleton-sm" :style="{ width: getSkeletonWidth() }"></div>
              </td>
            </tr>
          </template>
          <!-- Actual data rows -->
          <template v-else>
            <tr v-for="row in sortedFilteredRows" :key="row[idKey] || row.tempId" :class="{
              'table-warning': row.isNew,
              'row-edited': isRowEdited(row)
            }">
              <td v-if="$slots.actions">
                <slot name="actions" :row="row" />
              </td>
              <td v-if="selectable">
                <MaterialCheckbox :id="`row-${row[idKey]}`" :checked="isRowSelected(row[idKey])"
                  @change="toggleRow(row[idKey])" />
              </td>
              <td class="text-sm" v-for="col in columns" :key="col.key" @click="handleCellClick(row, col)"
                style="cursor: pointer;">
                <slot :name="`col-${col.key}`" :row="row">
                  <!-- Render different input types based on column configuration -->
                  <template v-if="editable && isEditing(row, col)">
                    <!-- Input field -->
                    <input v-if="getFieldType(col.key) === 'input'" :ref="el => { if (el) editInputRef = el }"
                      type="text" class="form-control form-control-sm" v-model="row[col.key]" @blur="stopEditing"
                      @keyup.enter="stopEditing" />
                    <!-- Select field -->
                    <select v-else-if="getFieldType(col.key) === 'select'" :ref="el => { if (el) editInputRef = el }"
                      class="form-select form-select-sm" v-model="row[col.key]" @blur="stopEditing"
                      @change="stopEditing">
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    <!-- Non-editable -->
                    <span v-else>{{ row[col.key] }}</span>
                  </template>
                  <template v-else>
                    {{ row[col.key] }}
                  </template>
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="pagination && !loading && pagination.totalRecords > 0" class="border-1 border-top border-gray mt-2">
        <PaginationFooter :pagination="pagination" @changePage="$emit('changePage', $event)"
          @updatePageSize="$emit('updatePageSize', $event)" />
      </div>
    </div>
    <!-- Empty state -->
    <div v-if="!loading && sortedFilteredRows.length === 0"
      class="text-center py-2 d-flex align-items-center justify-content-center flex-column">
      <NoDataFound v-if="showNoDataFoundImage" />
      <div class="" v-else> No Data Found</div>
    </div>
  </div>
</template>

<style scoped>
.table thead th {
  padding-inline: 0.5rem !important;
}

.table-warning {
  background-color: #fff3cd !important;
}

.row-edited {
  background-color: #fff3cd !important;
  border-left: 4px solid #ffc107 !important;
  transition: all 0.3s ease;
}

.row-edited:hover {
  background-color: #ffe69c !important;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  height: 16px;
  display: inline-block;
}

.skeleton-sm {
  height: 14px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
</style>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import MaterialCheckbox from "../common/MaterialCheckbox.vue";
import MaterialInput from "../common/MaterialInput.vue";
import PaginationFooter from "../PageLayout/PaginationFooter.vue";
import MaterialButton from "../common/MaterialButton.vue";
import NoDataFound from "../NoDataFound.vue";

const props = defineProps({
  heading: { type: String, },
  showNoDataFoundImage: { type: Boolean, default: true },
  rows: { type: Array, required: true },
  columns: { type: Array, required: true },
  idKey: { type: String, default: "id" },
  searchable: { type: Boolean, default: true },
  searchPlaceholder: { type: String, default: "Search..." },
  selectable: { type: Boolean, default: false },
  selected: { type: Array, default: () => [] },
  pagination: { type: [Object, Boolean], default: () => ({}) },
  sortable: { type: Boolean, default: true },
  setupWizard: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  skeletonRows: { type: Number, default: 5 },
  editable: { type: Boolean, default: false },
  fieldTypes: { type: Object, default: () => ({}) },
  editedRowIds: { type: Set, default: () => new Set() },
  serverSort: { type: Boolean, default: false },
  initialSortKey: { type: String, default: "" },
  initialSortOrder: { type: String, default: "asc" },
});

const emit = defineEmits([
  "update:selected",
  "cell-edited",
  "changePage",
  "updatePageSize",
  "sortChanged",
  "cellEdited",
  "searchChanged",
]);

const searchInput = ref("");
const searchQuery = ref("");
const rowsData = ref([...props.rows]);
const sortKey = ref(props.initialSortKey);
const sortOrder = ref(props.initialSortOrder);
const editingCell = ref(null);
let editInputRef = null;

const getSkeletonWidth = () => {
  const widths = ["60%", "70%", "80%", "90%", "100%"];
  return widths[Math.floor(Math.random() * widths.length)];
};

const getFieldType = (colKey) => props.fieldTypes[colKey] || "readonly";

const isRowEdited = (row) => {
  const rowId = row[props.idKey] || row.tempId;
  return props.editedRowIds.has(rowId);
};

const isEditing = (row, col) => {
  if (!editingCell.value) return false;
  const rowId = row[props.idKey] || row.tempId;
  return (
    editingCell.value.rowId === rowId &&
    editingCell.value.colKey === col.key
  );
};

const handleCellClick = async (row, col) => {
  if (!props.editable) return;
  const fieldType = getFieldType(col.key);
  if (fieldType === "readonly") return;
  const rowId = row[props.idKey] || row.tempId;
  editingCell.value = { rowId, colKey: col.key };
  emit("cell-edited", { row, column: col });
  await nextTick();
  if (editInputRef) editInputRef.focus();
};

const stopEditing = () => {
  editingCell.value = null;
  editInputRef = null;
};

/* Sorting logic */
const fetchSortedRows = async (key, order) => {
  const sorted = [...props.rows].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];
    if (av == null && bv == null) return 0;
    if (av == null) return order === "asc" ? -1 : 1;
    if (bv == null) return order === "asc" ? 1 : -1;
    if (typeof av === "number" && typeof bv === "number")
      return order === "asc" ? av - bv : bv - av;
    const as = String(av);
    const bs = String(bv);
    return order === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
  });
  return await new Promise((resolve) => setTimeout(() => resolve(sorted), 250));
};

watch(
  () => props.rows,
  async () => {
    rowsData.value = [...props.rows];
    if (props.sortable && sortKey.value) {
      rowsData.value = await fetchSortedRows(sortKey.value, sortOrder.value);
    }
  },
  { deep: true }
);

watch([sortKey, sortOrder], async () => {
  if (props.serverSort) {
    emit("sortChanged", { key: sortKey.value, order: sortOrder.value });
    return;
  }
  if (!props.sortable || !sortKey.value) {
    rowsData.value = [...props.rows];
    return;
  }
  rowsData.value = await fetchSortedRows(sortKey.value, sortOrder.value);
});

/* ✅ Apply search - emits to parent and filters locally as fallback */
const applySearch = () => {
  searchQuery.value = searchInput.value.trim();
  emit("searchChanged", searchQuery.value);
};

const clearSearch = () => {
  searchInput.value = "";
  searchQuery.value = "";
  emit("searchChanged", "");
};

/* Local filter fallback */
const filteredRows = computed(() => {
  const base = rowsData.value;
  if (!props.searchable || !searchQuery.value) return base;
  const q = searchQuery.value.toLowerCase();
  return base.filter((row) =>
    Object.values(row).some((v) => String(v).toLowerCase().includes(q))
  );
});

// ✅ Use props.selected instead of local selected ref
const isRowSelected = (id) => {
  return props.selected.includes(id);
};

const allSelected = computed(() => {
  if (filteredRows.value.length === 0) return false;
  return filteredRows.value.every(row => props.selected.includes(row[props.idKey]));
});

const toggleSelectAll = () => {
  if (allSelected.value) {
    // Deselect all visible rows
    const visibleIds = filteredRows.value.map(r => r[props.idKey]);
    const newSelection = props.selected.filter(id => !visibleIds.includes(id));
    emit("update:selected", newSelection);
  } else {
    // Select all visible rows
    const visibleIds = filteredRows.value.map(r => r[props.idKey]);
    const newSelection = [...new Set([...props.selected, ...visibleIds])];
    emit("update:selected", newSelection);
  }
};

const toggleRow = (id) => {
  const newSelection = props.selected.includes(id)
    ? props.selected.filter(s => s !== id)
    : [...props.selected, id];

  emit("update:selected", newSelection);
};

const sortedFilteredRows = computed(() => filteredRows.value);
</script>